import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { P2GBox } from '@parcel2go/core-ui';
import { P2GSidePanel } from '~/components/sidepanel';
import { ProductApiModel, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { useSmartSendApi } from '~/lib/api/smartsend/use-smartsend-api';
import { useMeApi } from '~/lib/api/me/use-me-api';
import useExternalSearch from '~/apps/smartsend/components/smartsend/client/useExternalSearch';
import { ExternalSearchBooked } from '~/apps/smartsend/lib/consts/ListyConsts';
import Loading from '~/components/loading/Loading';
import { SmartSendContext } from '~/apps/smartsend/lib/context/SmartSendContext';
import {
  AddNewItemAction,
  AddNewItemsAction,
  UpdateFilterAction
} from '~/apps/smartsend/lib/context/SmartSendContextModels';
import { getNamedFilter } from '~/apps/smartsend/lib/helpers/DefaultFilters';
import { uid } from '~/apps/smartsend/lib/helpers/uid';
import { SmartSendSubType } from '~/apps/smartsend/lib/consts/SmartSendSubType';
import useCreateItemsHelper, { CreateItemReq, CreateItemRes } from '~/apps/smartsend/lib/helpers/useItemCreateHelper';
import NewShipmentForm from './NewShipmentForm';
import { mapMeProductToProductApiModel } from './NewShipmentFormHelpers';
import useCountries from '~/hooks/use-countries';
import useGoto from '~/hooks/use-goto';
import { RoutingConstants } from '~/apps/smartsend/lib/consts/RoutingConstants';
import RenderCreateItemMessage from '../edit/RenderCreateItemMessage';
import { Tagged } from '../../Tagged';
import { CsvPanel } from '../panels/CsvPanel';
import QuickSendEntryNew from './QuickSendEntryNew';
import QuickSendEntryReturning from './QuickSendEntryReturning';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { useEventEmitter } from '~/apps/smartsend/lib/em/Hooks';
import { Consts, EditEventType } from '~/apps/smartsend/lib/em/EmitTypes';

type PanelView = 'loading' | 'entry' | 'create' | 'csv';

interface QuickSendPanelProps {
  children: React.ReactNode;
  onOpen?: () => void;
}

const QuickSendPanel = ({ children, onOpen }: QuickSendPanelProps) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const ctx = useContext(SmartSendContext);
  const { getCustomers, getPackages } = useSmartSendApi();
  const { createProduct, getProducts: getMeProducts } = useMeApi();
  const { getExternalSearchQueryCountByListyKey } = useExternalSearch();
  const { createItemsHelper } = useCreateItemsHelper();
  const { getLocalisedCountries } = useCountries();
  const { goTo, replaceLocationState } = useGoto();
  const { emit } = useEventEmitter<EditEventType>();

  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<PanelView>('loading');
  const [isNewUser, setIsNewUser] = useState(false);
  const [products, setProducts] = useState<ProductApiModel[]>([]);
  const [createMessages, setCreateMessages] = useState<string[]>([]);
  const [csvMessages, setCsvMessages] = useState<string[]>([]);
  const [initialProduct, setInitialProduct] = useState<ProductApiModel | null>(null);
  const [cameFromMyProducts, setCameFromMyProducts] = useState(false);
  const unmountedRef = useRef(false);

  const loadProductList = useCallback(
    () => getMeProducts().then((r) => (r.success ? r.result.products.map(mapMeProductToProductApiModel) : [])),
    [getMeProducts]
  );

  const handleOpen = useCallback(async () => {
    setOpen(true);
    setView('loading');
    onOpen?.();
    const [orderCount, productList] = await Promise.all([
      getExternalSearchQueryCountByListyKey(ExternalSearchBooked).catch(() => 1),
      loadProductList()
    ]);
    setIsNewUser(orderCount === 0);
    setProducts(productList);
    setView('entry');
  }, [getExternalSearchQueryCountByListyKey, loadProductList, onOpen]);

  // Deep-link from My Products' "Create Order" CTA - runs once, panel always mounts fresh for it.
  useEffect(() => {
    unmountedRef.current = false;
    const { createFromProductId, ...restState } = (location.state as Record<string, unknown>) ?? {};
    if (createFromProductId == null) return;
    // Strip only our key so it isn't replayed on back/refresh, without touching other state.
    replaceLocationState(Object.keys(restState).length ? restState : null);
    (async () => {
      setOpen(true);
      setView('loading');
      onOpen?.();
      const productList = await loadProductList();
      if (unmountedRef.current) return;
      setProducts(productList);
      setInitialProduct(productList.find((p) => p.Id === String(createFromProductId)) ?? null);
      setCameFromMyProducts(true);
      setView('create');
    })();
    return () => {
      unmountedRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    setOpen(false);
    setCreateMessages([]);
    setCsvMessages([]);
    setInitialProduct(null);
    setCameFromMyProducts(false);
  };

  const handleGoToEntry = () => {
    setView('entry');
    setCreateMessages([]);
    setCsvMessages([]);
    setInitialProduct(null);
    setCameFromMyProducts(false);
  };

  const handleCreateShipment = (product?: ProductApiModel) => {
    setInitialProduct(product ?? null);
    setView('create');
  };

  const handleBackFromCreate = () => {
    if (cameFromMyProducts) {
      handleClose();
      goTo(RoutingConstants.MyProducts);
      return;
    }
    handleGoToEntry();
  };

  const handleRestore = () => {
    handleClose();
    goTo(RoutingConstants.SmartSend);
    emit(Consts.Edit, { Id: null, Action: 'RESTORE' });
  };

  const saveCsvItems = async (items: CreateItemReq[]): Promise<CreateItemRes[]> => {
    const summary = await createItemsHelper(
      items,
      (ii) => {
        ctx.dispatch(new AddNewItemsAction(ii));
      },
      () => {},
      () => {}
    );
    if (summary.failed.length === 0) {
      if (summary.messages.length > 0) {
        setCsvMessages(summary.messages);
      } else {
        handleClose();
      }
    }
    return summary.failed;
  };

  const saveNewItem = (item: SmartSendItemApiModel) => {
    createItemsHelper(
      [{ id: uid(), item, raw: [] }],
      (items) => {
        const saved = items[0];
        if (saved) {
          ctx.dispatch(new AddNewItemAction(saved, !saved.Service?.Id));
          goTo(RoutingConstants.SmartSend);
        }
        ctx.dispatch(
          new UpdateFilterAction(SmartSendSubType, {
            ...ctx.state.filters[SmartSendSubType].filter,
            CurrentFilter: getNamedFilter('Post_Create')
          })
        );
      },
      () => {},
      (messages) => {
        if (messages.length > 0) {
          setCreateMessages(messages);
        } else {
          handleClose();
        }
      }
    );
  };

  const getPanelTitle = () => {
    switch (view) {
      case 'create':
        return t('QUICK_SEND_PANEL_TITLE_CREATE', { defaultValue: 'New Order' });
      case 'csv':
        return t('QUICK_SEND_PANEL_TITLE_CSV', { defaultValue: 'CSV Upload' });
      case 'entry':
        return t('QUICK_SEND_PANEL_TITLE_ENTRY_NEW_USER', { defaultValue: 'Create Order' });
      default:
        return '';
    }
  };

  return (
    <>
      <P2GBox onClick={handleOpen}>{children}</P2GBox>
      <P2GSidePanel open={open} onClose={handleClose} anchor="right" title={getPanelTitle()} width="690px">
        <Tagged tag="panel-quick-send">
          {view === 'loading' && <Loading />}
          {view === 'entry' && isNewUser && (
            <QuickSendEntryNew
              onCreateShipment={() => setView('create')}
              onCsvUpload={() => setView('csv')}
              onRestore={handleRestore}
            />
          )}
          {view === 'entry' && !isNewUser && (
            <QuickSendEntryReturning
              products={products}
              onCreateShipment={handleCreateShipment}
              onCsvUpload={() => setView('csv')}
              onRestore={handleRestore}
            />
          )}
          {view === 'csv' &&
            (csvMessages.length > 0 ? (
              <RenderCreateItemMessage messages={csvMessages} onClose={handleClose} />
            ) : (
              <CsvPanel onSave={saveCsvItems} close={handleGoToEntry} />
            ))}
          {view === 'create' &&
            (createMessages.length > 0 ? (
              <RenderCreateItemMessage messages={createMessages} onClose={handleClose} />
            ) : (
              <NewShipmentForm
                products={products}
                initialProduct={initialProduct}
                sender={ctx.state.customer}
                getCustomers={() => getCustomers().then((r) => (r.success ? r.result.Customers : []))}
                getCountries={() => getLocalisedCountries()}
                getPackages={() => getPackages().then((r) => (r.success ? r.result.Packages : []))}
                onSave={saveNewItem}
                onClose={handleBackFromCreate}
                createProduct={createProduct}
              />
            ))}
        </Tagged>
      </P2GSidePanel>
    </>
  );
};

export default QuickSendPanel;
